import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCart, upsertCartItem, deleteCartItem, clearCart, reserveStock, releaseReservation, } from './api';
// ---------------------------------------------------------------------------
// useCart
// ---------------------------------------------------------------------------
export function useCart() {
    const queryClient = useQueryClient();
    const { data, isLoading } = useQuery({
        queryKey: ['cart'],
        queryFn: getCart,
        // Treat stale items as [] so count badge never flashes wrong
        placeholderData: { items: [] },
        staleTime: 30_000,
    });
    const items = data?.items ?? [];
    // Shared optimistic helpers
    function snapshot() {
        return { items: queryClient.getQueryData(['cart'])?.items ?? [] };
    }
    function setOptimistic(next) {
        queryClient.setQueryData(['cart'], { items: next });
    }
    function rollback(prev) {
        queryClient.setQueryData(['cart'], { items: prev.items });
    }
    /*
     * add mutation - caller passes ABSOLUTE quantity (already computed against
     * the pre-mutation snapshot in `add()` below). This avoids the previous
     * double-add bug where mutationFn read the post-optimistic snapshot and
     * stacked the delta a second time.
     */
    const addMutation = useMutation({
        mutationFn: (params) => upsertCartItem(params.productProfileId, {
            publicSlug: params.publicSlug,
            quantity: params.quantity,
            imageUrl: params.imageUrl,
            displayName: params.displayName,
        }),
        onMutate: (params) => {
            const prev = snapshot();
            const existing = prev.items.find((i) => i.productProfileId === params.productProfileId);
            const newItems = existing
                ? prev.items.map((i) => i.productProfileId === params.productProfileId ? { ...i, quantity: params.quantity } : i)
                : [
                    ...prev.items,
                    {
                        productProfileId: params.productProfileId,
                        publicSlug: params.publicSlug,
                        quantity: params.quantity,
                        imageUrl: params.imageUrl,
                        displayName: params.displayName,
                    },
                ];
            setOptimistic(newItems);
            return prev;
        },
        onError: (_err, _item, ctx) => { if (ctx)
            rollback(ctx); },
        onSuccess: (res) => {
            queryClient.setQueryData(['cart'], { items: res.items });
        },
    });
    // remove mutation
    const removeMutation = useMutation({
        mutationFn: (productProfileId) => deleteCartItem(productProfileId),
        onMutate: (productProfileId) => {
            const prev = snapshot();
            setOptimistic(prev.items.filter((i) => i.productProfileId !== productProfileId));
            return prev;
        },
        onError: (_err, _id, ctx) => { if (ctx)
            rollback(ctx); },
        onSuccess: (res) => {
            queryClient.setQueryData(['cart'], { items: res.items });
        },
    });
    // setQuantity mutation - absolute
    const setQuantityMutation = useMutation({
        mutationFn: ({ productProfileId, quantity }) => {
            const item = snapshot().items.find((i) => i.productProfileId === productProfileId);
            if (!item)
                return Promise.resolve({ items: snapshot().items });
            if (quantity <= 0)
                return deleteCartItem(productProfileId);
            return upsertCartItem(productProfileId, {
                publicSlug: item.publicSlug,
                quantity,
                imageUrl: item.imageUrl,
                displayName: item.displayName,
            });
        },
        onMutate: ({ productProfileId, quantity }) => {
            const prev = snapshot();
            const next = quantity <= 0
                ? prev.items.filter((i) => i.productProfileId !== productProfileId)
                : prev.items.map((i) => i.productProfileId === productProfileId ? { ...i, quantity } : i);
            setOptimistic(next);
            return prev;
        },
        onError: (_err, _vars, ctx) => { if (ctx)
            rollback(ctx); },
        onSuccess: (res) => {
            queryClient.setQueryData(['cart'], { items: res.items });
        },
    });
    // clear mutation
    const clearMutation = useMutation({
        mutationFn: clearCart,
        onMutate: () => {
            const prev = snapshot();
            setOptimistic([]);
            return prev;
        },
        onError: (_err, _v, ctx) => { if (ctx)
            rollback(ctx); },
        onSuccess: (res) => {
            queryClient.setQueryData(['cart'], { items: res.items });
        },
    });
    /*
     * Public add() preserves the delta-add caller convention. We resolve the new
     * absolute quantity HERE (against the current cache) and pass it to the
     * mutation so the server receives the correct total in a single round trip.
     *
     * Phase G - also kicks off a `POST /cart/reserve` for the absolute delta so
     * the storefront holds the stock for the Stripe Checkout window. Fire-and-
     * forget: if the reserve call fails we leave the cart line in place (the
     * order create path will still re-check availability and reject if truly
     * empty). On success we patch the line's `reservationId` into the cache so
     * the checkout payload picks it up.
     */
    function patchReservationId(productProfileId, reservationId) {
        const curr = queryClient.getQueryData(['cart']);
        if (!curr)
            return;
        queryClient.setQueryData(['cart'], {
            items: curr.items.map((i) => i.productProfileId === productProfileId ? { ...i, reservationId } : i),
        });
    }
    function fireReleaseIfPresent(item) {
        if (!item?.reservationId)
            return;
        void releaseReservation(item.reservationId).catch(() => {
            // Best-effort release. If the network is down the TTL sweep covers
            // us - worst case the hold lingers for ~10 min.
        });
    }
    const add = useCallback((item) => {
        const current = snapshot().items.find((i) => i.productProfileId === item.productProfileId);
        const newQty = (current?.quantity ?? 0) + item.quantity;
        addMutation.mutate({
            productProfileId: item.productProfileId,
            publicSlug: item.publicSlug,
            quantity: newQty,
            imageUrl: item.imageUrl,
            displayName: item.displayName,
        });
        // Phase G - reserve the DELTA (not the absolute) so a second add
        // stacks on top of the first hold. Release the previous hold (if
        // any) once we have a successful new one to avoid double-holding
        // for the same line while still being race-safe. After reservation
        // succeeds, persist the id on the server-side cart so a page
        // refresh keeps it.
        void (async () => {
            try {
                const res = await reserveStock(item.productProfileId, item.quantity);
                patchReservationId(item.productProfileId, res.reservationId);
                fireReleaseIfPresent(current);
                // Best-effort: re-upsert with the new reservationId so the
                // backend cart row carries it across page reloads. If this
                // fails the cache still has it for the current session.
                try {
                    await upsertCartItem(item.productProfileId, {
                        publicSlug: item.publicSlug,
                        quantity: newQty,
                        imageUrl: item.imageUrl,
                        displayName: item.displayName,
                        reservationId: res.reservationId,
                    });
                }
                catch {
                    // ignore - cache already patched
                }
            }
            catch {
                // OOI or network error - leave the line as-is; the cart pricing
                // call will surface the new availability ceiling.
            }
        })();
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addMutation.mutate, queryClient]);
    const remove = useCallback((productProfileId) => {
        const current = snapshot().items.find((i) => i.productProfileId === productProfileId);
        removeMutation.mutate(productProfileId);
        // Phase G - release the hold so the unit returns to available stock
        // for other customers immediately.
        fireReleaseIfPresent(current);
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [removeMutation.mutate, queryClient]);
    const setQuantity = useCallback((productProfileId, quantity) => {
        setQuantityMutation.mutate({ productProfileId, quantity });
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setQuantityMutation.mutate]);
    const clear = useCallback(() => { clearMutation.mutate(); }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clearMutation.mutate]);
    return { items, add, remove, setQuantity, clear, isLoading };
}
// ---------------------------------------------------------------------------
// useCartCount
// ---------------------------------------------------------------------------
/** Total item count across all cart lines. */
export function useCartCount() {
    const { items } = useCart();
    return items.reduce((sum, i) => sum + i.quantity, 0);
}
// ---------------------------------------------------------------------------
// useMigrateLegacyCart
// ---------------------------------------------------------------------------
/** One-time migration: reads the old zustand persist payload from localStorage
 *  and upserts each item on the server. Flag-guarded so it never re-runs. */
export function useMigrateLegacyCart() {
    const queryClient = useQueryClient();
    // Cannot use useEffect here directly - callers import this hook and call it
    // at component mount. The actual effect is inside a dedicated component.
    // This hook is intentionally thin; see MigrateLegacyCart component below.
    void queryClient; // referenced so the import is used
}
/** Mount this once at the app root (Layout.tsx). */
export function MigrateLegacyCart() {
    const queryClient = useQueryClient();
    // Run once per session
    if (typeof window !== 'undefined') {
        const migrationFlag = 'lpc-cart-migrated';
        if (!sessionStorage.getItem(migrationFlag)) {
            const raw = localStorage.getItem('lpc-cart');
            if (raw) {
                try {
                    const payload = JSON.parse(raw);
                    const legacyItems = payload?.state?.items ?? [];
                    if (legacyItems.length > 0) {
                        // Fire-and-forget: upsert all legacy items sequentially
                        void (async () => {
                            try {
                                for (const item of legacyItems) {
                                    await upsertCartItem(item.productProfileId, {
                                        publicSlug: item.publicSlug,
                                        quantity: item.quantity,
                                        imageUrl: item.imageUrl,
                                        displayName: item.displayName,
                                    });
                                }
                                localStorage.removeItem('lpc-cart');
                                sessionStorage.setItem(migrationFlag, '1');
                                void queryClient.invalidateQueries({ queryKey: ['cart'] });
                            }
                            catch {
                                // Leave localStorage intact so retry happens next session
                            }
                        })();
                    }
                    else {
                        localStorage.removeItem('lpc-cart');
                        sessionStorage.setItem(migrationFlag, '1');
                    }
                }
                catch {
                    // Malformed JSON - remove and mark done
                    localStorage.removeItem('lpc-cart');
                    sessionStorage.setItem(migrationFlag, '1');
                }
            }
            else {
                sessionStorage.setItem(migrationFlag, '1');
            }
        }
    }
    return null;
}
